"use client";

import { useEffect, useRef, useState } from "react";
import { GOOGLE_PAY_SDK_URL, IS_PAYPAL_SANDBOX } from "@/lib/config";

interface GooglePayButtonProps {
  sdk: PayPalV6Instance;
  totalAmount: string;
  currencyCode: string;
  disabled: boolean;
  createOrder: () => Promise<string>;
  captureAndNotify: (orderId: string) => Promise<void>;
  onError?: (err: { code?: string; message: string }) => void;
  /**
   * Checkout-wide in-progress guard shared with the PayPal/card buttons.
   * When provided, a Google Pay tap while ANY rail is mid-payment is
   * ignored (and vice versa) — closes the cross-rail double-payment gap
   * (CHECKOUT_RETRY.md §Known Limitations, parity audit 2026-07-04).
   */
  paymentInProgressRef?: { current: boolean };
}

// Singleton: load Google Pay JS once
let googlePayScriptPromise: Promise<void> | null = null;

function loadGooglePayScript(): Promise<void> {
  if (googlePayScriptPromise) return googlePayScriptPromise;

  if (typeof window !== "undefined" && window.google?.payments?.api?.PaymentsClient) {
    return Promise.resolve();
  }

  googlePayScriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GOOGLE_PAY_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      googlePayScriptPromise = null;
      reject(new Error("Failed to load Google Pay SDK"));
    };
    document.head.appendChild(script);
  });

  return googlePayScriptPromise;
}

export default function GooglePayButton({
  sdk,
  totalAmount,
  currencyCode,
  disabled,
  createOrder,
  captureAndNotify,
  onError,
  paymentInProgressRef,
}: GooglePayButtonProps) {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for dynamic data
  const createOrderRef = useRef(createOrder);
  createOrderRef.current = createOrder;
  const captureRef = useRef(captureAndNotify);
  captureRef.current = captureAndNotify;
  const totalAmountRef = useRef(totalAmount);
  totalAmountRef.current = totalAmount;
  const currencyCodeRef = useRef(currencyCode);
  currencyCodeRef.current = currencyCode;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Prevents concurrent payment flows. Prefer the parent's shared
  // checkout-wide ref so the guard also covers cross-rail concurrency;
  // local ref is the standalone fallback.
  const localPaymentInProgressRef = useRef(false);
  const isPaymentInProgressRef = paymentInProgressRef ?? localPaymentInProgressRef;

  // Container for the programmatically-created Google Pay button
  const containerRef = useRef<HTMLDivElement>(null);

  // Refs for PayPal Google Pay session and config
  const googlePaySessionRef = useRef<GooglePayPayPalSession | null>(null);
  const googlePayConfigRef = useRef<any>(null);

  // Initialize: load Google Pay script + create session + check readiness + create button
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        // Load Google Pay JS
        await loadGooglePayScript();
        if (cancelled) return;

        // Create PayPal Google Pay session
        const googlePaySession = sdk.createGooglePayOneTimePaymentSession();
        googlePaySessionRef.current = googlePaySession;


        // Get config from PayPal
        const config = await googlePaySession.getGooglePayConfig();
        if (cancelled) return;
        googlePayConfigRef.current = config;

        console.log("[Google Pay] Config:", {
          apiVersion: config.apiVersion,
          merchantInfo: config.merchantInfo,
          countryCode: config.countryCode,
        });

        // Create Google Pay client with payment authorization callback
        const paymentsClient = new window.google!.payments.api.PaymentsClient({
          environment: IS_PAYPAL_SANDBOX ? "TEST" : "PRODUCTION",
          paymentDataCallbacks: {
            onPaymentAuthorized: async (paymentData: any) => {
              try {
                console.log("[Google Pay] onPaymentAuthorized called, creating order...");
                const orderId = await createOrderRef.current();
                console.log("[Google Pay] Order created:", orderId);

                console.log("[Google Pay] Calling confirmOrder...");
                const confirmResult = await googlePaySessionRef.current!.confirmOrder({
                  orderId,
                  paymentMethodData: paymentData.paymentMethodData,
                });
                console.log("[Google Pay] confirmOrder result:", JSON.stringify(confirmResult));

                if (confirmResult.status === "PAYER_ACTION_REQUIRED") {
                  // PayPal requires 3D Secure — use SDK's built-in iframe overlay.
                  // initiatePayerAction renders a full-screen 3DS challenge on top of the page,
                  // preserving all JS context. No redirect, no popup.
                  console.log("[Google Pay] 3DS required, calling initiatePayerAction for order:", orderId);
                  await (googlePaySessionRef.current as any).initiatePayerAction({ orderId });
                  console.log("[Google Pay] 3DS completed, capturing order:", orderId);
                }

                await captureRef.current(orderId);
                // Guard is NOT released here: on the ERROR branch Google Pay
                // keeps the sheet open and re-authorizable, and even on
                // SUCCESS the sheet is still up — the checkout-wide guard
                // must hold until loadPaymentData settles (sheet closed),
                // else another rail can start under a live wallet sheet.
                return { transactionState: "SUCCESS" };
              } catch (err: any) {
                console.error("[Google Pay] Authorization error:", err);
                console.error("[Google Pay] Error details:", JSON.stringify(err, Object.getOwnPropertyNames(err)));
                // Surface to parent error banner so the buyer sees what happened
                onErrorRef.current?.({
                  code: err?.code,
                  message: err?.message || "Google Pay payment failed",
                });
                return {
                  transactionState: "ERROR",
                  error: { message: err.message || "Payment failed" },
                };
              }
            },
          },
        });

        // Check if Google Pay is available
        const readyToPay = await paymentsClient.isReadyToPay({
          allowedPaymentMethods: config.allowedPaymentMethods,
          apiVersion: config.apiVersion,
          apiVersionMinor: config.apiVersionMinor,
        });

        if (cancelled) return;

        if (!readyToPay.result) {
          console.log("[Google Pay] Not ready to pay on this device");
          return;
        }

        // Create Google Pay button and append to container
        const button = paymentsClient.createButton({
          buttonColor: "black",
          buttonRadius: 30,
          onClick: () => {
            if (isPaymentInProgressRef.current) {
              console.log("[Google Pay] Payment already in progress, ignoring click");
              return;
            }
            const cfg = googlePayConfigRef.current;
            if (!cfg) return;
            isPaymentInProgressRef.current = true;

            const paymentDataRequest = {
              apiVersion: cfg.apiVersion,
              apiVersionMinor: cfg.apiVersionMinor,
              allowedPaymentMethods: cfg.allowedPaymentMethods,
              merchantInfo: cfg.merchantInfo,
              transactionInfo: {
                countryCode: cfg.countryCode,
                currencyCode: currencyCodeRef.current,
                totalPriceStatus: "FINAL",
                totalPrice: totalAmountRef.current,
                totalPriceLabel: "Total",
              },
              callbackIntents: ["PAYMENT_AUTHORIZATION"],
            };

            paymentsClient.loadPaymentData(paymentDataRequest)
              .then(() => {
                // Sheet closed (final success or final failure) — release
                // the checkout-wide guard HERE, not inside onPaymentAuthorized
                // (whose ERROR branch keeps the sheet open + re-authorizable).
                isPaymentInProgressRef.current = false;
              })
              .catch((err: any) => {
                isPaymentInProgressRef.current = false;
                // User closed the Google Pay sheet — not a real error
                if (err.statusCode === "CANCELED") {
                  console.log("[Google Pay] Payment cancelled by user");
                } else {
                  console.error("[Google Pay] loadPaymentData error:", err);
                }
              });
          },
          buttonSizeMode: "fill",
        });

        if (cancelled) return;

        // Append button to container
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
          containerRef.current.appendChild(button);
        }

        console.log("[Google Pay] Button created and ready");
        setIsReady(true);
      } catch (err: any) {
        if (!cancelled) {
          console.error("[Google Pay] Init failed:", err);
          setError(err.message || "Google Pay initialization failed");
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, [sdk]);

  if (error) {
    return (
      <div className="text-sm text-red-600 dark:text-red-400 text-center py-1">
        Google Pay unavailable
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="mt-2"
      style={{
        height: 55,
        ...(disabled ? { pointerEvents: "none", opacity: 0.5 } : {}),
        // Hide container until button is ready (prevents layout shift)
        ...(!isReady ? { display: "none" } : {}),
      }}
    />
  );
}
